import { forwardRef, type CSSProperties, type InputHTMLAttributes, type ReactNode } from "react";

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Hide the visual label but keep it for screen readers. */
  visuallyHiddenLabel?: boolean;
}

let idCounter = 0;
const genId = () => `gt-ti-${++idCounter}`;

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, hint, error, id, visuallyHiddenLabel, style, ...rest },
  ref,
) {
  const resolvedId = id ?? genId();
  const hintId = hint ? `${resolvedId}-hint` : undefined;
  const errorId = error ? `${resolvedId}-err` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const labelStyle: CSSProperties = visuallyHiddenLabel
    ? {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        overflow: "hidden",
        clip: "rect(0,0,0,0)",
        whiteSpace: "nowrap",
        border: 0,
      }
    : {
        display: "block",
        fontSize: 13,
        fontWeight: 600,
        marginBlockEnd: 6,
        color: "var(--gt-sys-text)",
      };

  const inputStyle: CSSProperties = {
    width: "100%",
    paddingBlock: 10,
    paddingInline: 12,
    background: "var(--gt-sys-surface)",
    color: "var(--gt-sys-text)",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: error ? "var(--gt-sys-danger)" : "var(--gt-sys-border)",
    borderRadius: "var(--gt-sys-radius-md)",
    outline: "none",
    transition: "border-color var(--gt-ref-motion-duration-fast) var(--gt-ref-motion-ease-out)",
    ...style,
  };

  return (
    <div>
      <label htmlFor={resolvedId} style={labelStyle}>
        {label}
      </label>
      <input
        {...rest}
        ref={ref}
        id={resolvedId}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        style={inputStyle}
      />
      {hint ? (
        <div
          id={hintId}
          style={{ marginBlockStart: 6, fontSize: 12, color: "var(--gt-sys-text-muted)" }}
        >
          {hint}
        </div>
      ) : null}
      {error ? (
        <div
          id={errorId}
          role="alert"
          style={{ marginBlockStart: 6, fontSize: 12, color: "var(--gt-sys-danger)" }}
        >
          {error}
        </div>
      ) : null}
    </div>
  );
});
